import axiosInstance from "./axiosInstance";
import csrOutreachService from "./csrOutreachService";
import chatSupportLogService from "./chatSupportLogService";
import { getAdminPanelRole } from "../../utils/adminPanelRole";

const FETCH_TIMEOUT_MS = 12000;

function todayLocalDateKey() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function withTimeout(promise, ms = FETCH_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Request timed out")), ms);
    }),
  ]);
}

function mapCsrTargetsResponse(data, canViewTeam, adminId) {
  if (!data) return null;
  const targetDate = data.targetDate || todayLocalDateKey();
  const rows = data.agents || [];
  const mineRow =
    rows.find((r) => Number(r.agentId) === Number(adminId)) ||
    rows.find((r) => r.isTeamLeadSelf) ||
    (rows.length === 1 ? rows[0] : null);

  const toSummary = (row) => {
    const t = row?.targets;
    return {
      targetMode: t?.targetMode === "calls_only" ? "calls_only" : "calls_and_premium",
      callsTarget: Number(t?.callsTarget) || 0,
      premiumTarget: Number(t?.premiumTarget) || 0,
      targetsSet: Boolean(row?.targetsSet && t),
    };
  };

  return {
    targetDate,
    visible: true,
    csr: {
      canViewTeam: Boolean(canViewTeam),
      mine: mineRow
        ? {
            agentId: mineRow.agentId,
            agentName: mineRow.agentName,
            targets: toSummary(mineRow),
            actuals: mineRow.actuals || { callsCount: 0, premiumCount: 0 },
          }
        : null,
      team: canViewTeam
        ? rows.map((row) => ({
            agentId: row.agentId,
            agentName: row.isTeamLeadSelf
              ? `${row.agentName || "Team lead"} (You)`
              : row.agentName,
            isTeamLeadSelf: Boolean(row.isTeamLeadSelf),
            targets: toSummary(row),
            actuals: row.actuals || { callsCount: 0, premiumCount: 0 },
          }))
        : [],
    },
  };
}

function mapChatTargetsResponse(data, canViewTeam, adminId) {
  if (!data) return null;
  const targetDate = data.targetDate || todayLocalDateKey();
  const adminDetails = getAdminDetailsFromStorage();
  const resolvedAdminId = adminId || adminDetails.id;
  const toSummary = (t, targetsSet) => ({
    chatMessages: Number(t?.chatMessages) || 0,
    emailsAdded: Number(t?.emailsAdded) || 0,
    socialResponses: Number(t?.socialResponses) || 0,
    randomRequests: Number(t?.randomRequests) || 0,
    targetsSet: Boolean(targetsSet),
  });

  const team = (data.teamTargets || []).map((row) => ({
    agentId: row.agentId,
    agentName: row.agentName,
    isTeamLeadSelf: Number(row.agentId) === Number(resolvedAdminId),
    targets: toSummary(row, true),
  }));

  return {
    targetDate,
    visible: true,
    chatSupport: {
      canViewTeam: Boolean(canViewTeam),
      mine: {
        agentId: data.agentId || resolvedAdminId,
        agentName: adminDetails.name,
        targets: toSummary(data.targets, data.targetsSet),
      },
      team: canViewTeam ? team : [],
    },
  };
}

function getAdminDetailsFromStorage() {
  try {
    const raw = localStorage.getItem("adminUserDetails");
    if (!raw) return { id: null, name: null };
    const parsed = JSON.parse(raw);
    const id = Number(parsed?.id);
    const name = [parsed?.firstName, parsed?.lastName].filter(Boolean).join(" ").trim() || null;
    return {
      id: Number.isFinite(id) ? id : null,
      name,
    };
  } catch {
    return { id: null, name: null };
  }
}

function getAdminIdFromStorage() {
  return getAdminDetailsFromStorage().id;
}

async function fetchFallbackTargets() {
  const role = getAdminPanelRole();
  const adminId = getAdminIdFromStorage();
  const targetDate = todayLocalDateKey();
  const payload = { targetDate, visible: false, chatSupport: null, csr: null };

  const isLead = role === "csr team lead";
  const isCsr = role === "csr" || isLead;
  const isChat = role === "chat support" || isLead || role === "admin" || role === "developer";

  if (isCsr) {
    try {
      const csrData = await csrOutreachService.getTargets({ date: targetDate });
      const mapped = mapCsrTargetsResponse(
        csrData,
        isLead || role === "admin" || role === "developer",
        adminId
      );
      if (mapped?.csr) {
        payload.csr = mapped.csr;
        payload.visible = true;
      }
    } catch {
      // ignore
    }
  }

  if (isChat && !payload.chatSupport) {
    try {
      const chatData = await chatSupportLogService.getTargets({ date: targetDate });
      const mapped = mapChatTargetsResponse(
        chatData,
        role === "csr team lead" || role === "admin" || role === "developer",
        adminId
      );
      if (mapped?.chatSupport) {
        payload.chatSupport = mapped.chatSupport;
        payload.visible = true;
      }
    } catch {
      // ignore
    }
  }

  return payload.visible ? payload : null;
}

const dailyTargetsService = {
  getHeaderTargets: async (params = {}) => {
    try {
      const res = await withTimeout(
        axiosInstance.get("/admin/daily-targets/header", { params })
      );
      if (res?.data?.visible) return res.data;
      if (res?.data?.success) return res.data;
    } catch {
      // fall through to legacy endpoints
    }
    return fetchFallbackTargets();
  },
};

export default dailyTargetsService;
